import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { BsCheckCircle, BsXCircle } from 'react-icons/bs'

import { useConfig } from '../../contexts/ConfigContext'
import { Button } from '../Shared/Buttons'
import setting from '../../setting.json'

import {
  TxPage,
  BillCard,
  BillHeader,
  LogoCircle,
  LogoPlaceholder,
  BillTitle,
  StatusBanner,
  AmountBlock,
  BillBody,
  DetailRow,
  DetailTitle,
  DetailVal,
  StatusVal,
  BillFooter,
  ErrorCard,
  LoadingCard
} from './styles'

const SUCCESS_CODES = ['CAPTURED', 'ACCEPT', 'SUCCESS']
const POLL_MS = 2000
const POLL_MAX = 15

const looksLikeGatewayToken = (value) => {
  if (!value) return false
  const v = String(value)
  return v.length > 40 && /^[0-9a-fA-F]+$/.test(v)
}

const formatAmount = (amount) => {
  const n = Number(amount)
  if (Number.isNaN(n)) return '—'
  return n.toFixed(3)
}

const formatPayDate = (details) => {
  if (!details) return '—'
  if (details.paidOn) return details.paidOn
  if (details.date && details.time) return `${details.date} ${details.time}`
  if (details.date) return details.date
  return '—'
}

const isSuccessDetails = (details) => {
  if (!details) return false
  const code = String(details.resultCode || '').toUpperCase()
  if (SUCCESS_CODES.includes(code)) return true
  if (details.status === true || details.status === 1 || details.status === '1') return true
  if (String(details.message || '').toLowerCase().includes('success')) return true
  return false
}

const hasFinalResult = (details) => {
  if (!details) return false
  return Boolean(
    details.resultCode ||
    details.paymentId ||
    details.paymentToken ||
    details.transactionId ||
    details.message
  )
}

const resolveReference = (routeReference, params) => {
  const candidates = [
    routeReference,
    params.get('ref'),
    params.get('orderReferenceNumber'),
    params.get('reference')
  ].filter(Boolean)

  for (const c of candidates) {
    if (!looksLikeGatewayToken(c)) return String(c)
  }

  try {
    const saved = sessionStorage.getItem('payLinkReference')
    if (saved) return saved
  } catch (e) { /* ignore */ }

  return ''
}

const mapDetails = (d, fallbackRef) => ({
  success: isSuccessDetails(d),
  resultCode: d.resultCode || '—',
  message: d.message || '—',
  PayId: d.paymentToken || d.transactionId || d.id || '—',
  PaymentId: d.paymentId || '—',
  transactionId: d.transactionId || '—',
  Amount: formatAmount(d.amount),
  MerchUdf3: formatPayDate(d),
  orderReferenceNumber: d.orderReferenceNumber || fallbackRef || '—',
  method: d.variable2 || d.method || '—'
})

/** Flatten Hesabe checkout response from POST/GET /pay/response */
const mapFromGatewayResponse = (payload) => {
  if (!payload) return null
  const root = payload.data || payload
  const inner = root?.response?.data || root?.response || root
  if (!inner || typeof inner !== 'object') return null
  return {
    status: root.status ?? inner.status,
    code: root.code ?? inner.code,
    message: root.message || inner.message,
    resultCode: inner.resultCode,
    paymentToken: inner.paymentToken,
    paymentId: inner.paymentId,
    paidOn: inner.paidOn,
    orderReferenceNumber: inner.orderReferenceNumber,
    transactionId: inner.transactionId,
    bankReferenceId: inner.bankReferenceId,
    variable1: inner.variable1,
    variable2: inner.variable2,
    variable3: inner.variable3,
    variable4: inner.variable4,
    variable5: inner.variable5,
    method: inner.method,
    amount: inner.amount
  }
}

const fetchPayDetails = async (ref) => {
  const response = await fetch(`${setting.server}/pay/${encodeURIComponent(ref)}`, {
    method: 'GET',
    headers: {
      'cache-control': 'no-cache',
      'Content-Type': 'application/json',
      Accept: 'application/json'
    }
  })
  const result = await response.json()
  if ((result?.success === 1 || result?.success === true) && result?.order_details) {
    return result.order_details
  }
  return null
}

/** Tell Laravel to decrypt gateway `data` and update the bill row */
const processGatewayCallback = async (encryptedData) => {
  const url = `${setting.server}/pay/response?data=${encodeURIComponent(encryptedData)}`
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'cache-control': 'no-cache',
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }
  })
  const result = await response.json()
  return mapFromGatewayResponse(result)
}

export const Transaction = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const { search } = useLocation()
  const { reference: routeReference } = useParams()
  const [configState] = useConfig()

  const params = useMemo(() => new URLSearchParams(search), [search])
  const gatewayData = useMemo(() => {
    const raw = params.get('data') || params.get('encrp')
    return looksLikeGatewayToken(raw) ? raw : ''
  }, [params])

  const reference = useMemo(
    () => resolveReference(routeReference, params),
    [routeReference, params]
  )

  const logoSrc = !configState?.loading ? configState?.configs?.logo : null
  const currency = configState?.configs?.currency || 'KWD'

  const [loading, setLoading] = useState(true)
  const [polling, setPolling] = useState(false)
  const [paymentDetails, setPaymentDetails] = useState(null)
  const [loadError, setLoadError] = useState('')
  const [activeRef, setActiveRef] = useState(reference)

  const applyDetails = useCallback((details, ref) => {
    setPaymentDetails(mapDetails(details, ref))
  }, [])

  // Credit-card hosted checkout returns inside an iframe — show result on the top window.
  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window.top && window.top !== window.self) {
        window.top.location.replace(window.location.href)
      }
    } catch (e) {
      // Cross-origin parent — ignore
    }
  }, [])

  useEffect(() => {
    let active = true
    let timer = null
    let attempts = 0

    const load = async () => {
      setLoading(true)
      setLoadError('')
      setPolling(false)

      let ref = reference
      let detailsFromGateway = null

      // Hesabe redirected here with ?data=… — update backend, then show bill
      if (gatewayData) {
        try {
          detailsFromGateway = await processGatewayCallback(gatewayData)
          if (!active) return
          if (detailsFromGateway?.orderReferenceNumber) {
            ref = String(detailsFromGateway.orderReferenceNumber)
            try {
              sessionStorage.setItem('payLinkReference', ref)
            } catch (e) { /* ignore */ }
            setActiveRef(ref)
            history.replace(`/transaction/${encodeURIComponent(ref)}`)
          }
        } catch (err) {
          console.log(err, 'pay/response callback error')
        }
      }

      if (!ref && detailsFromGateway) {
        applyDetails(detailsFromGateway, detailsFromGateway.orderReferenceNumber || '')
        setLoading(false)
        return
      }

      if (!ref) {
        setLoading(false)
        setPaymentDetails(null)
        setLoadError(t('Missing transaction data'))
        return
      }

      setActiveRef(ref)

      try {
        let details = await fetchPayDetails(ref)
        if (!active) return

        // Prefer fresh gateway payload if bill API is still stale
        if ((!details || !hasFinalResult(details)) && detailsFromGateway && hasFinalResult(detailsFromGateway)) {
          details = detailsFromGateway
        }

        if (!details) {
          setPaymentDetails(null)
          setLoadError(t('Unable to read payment response'))
          setLoading(false)
          return
        }

        applyDetails(details, ref)
        setLoading(false)

        if (!hasFinalResult(details)) {
          setPolling(true)
          const tick = async () => {
            attempts += 1
            try {
              const next = await fetchPayDetails(ref)
              if (!active) return
              if (next) {
                applyDetails(next, ref)
                if (hasFinalResult(next) || attempts >= POLL_MAX) {
                  setPolling(false)
                  try { sessionStorage.removeItem('payLinkReference') } catch (e) { /* ignore */ }
                  return
                }
              } else if (attempts >= POLL_MAX) {
                setPolling(false)
                return
              }
            } catch (err) {
              console.log(err, 'pay poll error')
              if (attempts >= POLL_MAX) {
                setPolling(false)
                return
              }
            }
            timer = setTimeout(tick, POLL_MS)
          }
          timer = setTimeout(tick, POLL_MS)
        } else {
          try { sessionStorage.removeItem('payLinkReference') } catch (e) { /* ignore */ }
        }
      } catch (err) {
        console.log(err, 'transaction load error')
        if (!active) return
        if (detailsFromGateway) {
          applyDetails(detailsFromGateway, ref)
          setLoading(false)
          return
        }
        setPaymentDetails(null)
        setLoadError(t('Unable to read payment response'))
        setLoading(false)
      }
    }

    load()
    return () => {
      active = false
      if (timer) clearTimeout(timer)
    }
  }, [reference, gatewayData, t, applyDetails, history])

  const goHome = () => {
    history.push('/home')
  }

  const success = Boolean(paymentDetails?.success)
  const d = paymentDetails

  if (!reference && !gatewayData && !activeRef) {
    return (
      <TxPage>
        <ErrorCard>{t('Missing transaction data')}</ErrorCard>
      </TxPage>
    )
  }

  if (loading && !paymentDetails) {
    return (
      <TxPage>
        <LoadingCard>{t('Please wait')}</LoadingCard>
      </TxPage>
    )
  }

  if (loadError && !paymentDetails) {
    return (
      <TxPage>
        <ErrorCard>{loadError}</ErrorCard>
        <div style={{ marginTop: 12, width: '100%', maxWidth: 400 }}>
          <Button color='primary' width='100%' onClick={goHome}>
            {t('Go Home')}
          </Button>
        </div>
      </TxPage>
    )
  }

  return (
    <TxPage>
      <BillCard>
        <BillHeader>
          {logoSrc ? (
            <LogoCircle>
              <img src={logoSrc} alt='logo' />
            </LogoCircle>
          ) : (
            <LogoPlaceholder />
          )}
          <BillTitle>{t('Transaction Details')}</BillTitle>
        </BillHeader>

        <StatusBanner success={success}>
          {success ? <BsCheckCircle /> : <BsXCircle />}
          <div className='title'>
            {success ? t('Transaction Successful') : t('Transaction Failed')}
          </div>
          {polling && <div className='hint'>{t('Updating payment status')}</div>}
        </StatusBanner>

        <AmountBlock>
          <div className='label'>{t('Amount')}</div>
          <div className='value'>
            <span>{d.Amount}</span>
            <span className='currency'>{t(currency)}</span>
          </div>
        </AmountBlock>

        <BillBody>
          <DetailRow>
            <DetailTitle>{t('Result')}</DetailTitle>
            <DetailVal>
              <StatusVal success={success}>
                {d.message && d.message !== '—'
                  ? d.message
                  : (success ? t('Successful') : t('Unsuccessful'))}
              </StatusVal>
            </DetailVal>
          </DetailRow>

          <DetailRow>
            <DetailTitle>{t('Result Code')}</DetailTitle>
            <DetailVal>{d.resultCode}</DetailVal>
          </DetailRow>

          <DetailRow>
            <DetailTitle>{t('Reference No')}</DetailTitle>
            <DetailVal>{d.orderReferenceNumber}</DetailVal>
          </DetailRow>

          <DetailRow>
            <DetailTitle>{t('Transaction No')}</DetailTitle>
            <DetailVal>{d.PayId}</DetailVal>
          </DetailRow>

          <DetailRow>
            <DetailTitle>{t('Payment Id')}</DetailTitle>
            <DetailVal>{d.PaymentId}</DetailVal>
          </DetailRow>

          <DetailRow>
            <DetailTitle>{t('Bank Transaction Id')}</DetailTitle>
            <DetailVal>{d.transactionId}</DetailVal>
          </DetailRow>

          <DetailRow>
            <DetailTitle>{t('Date')}</DetailTitle>
            <DetailVal>{d.MerchUdf3}</DetailVal>
          </DetailRow>

          <DetailRow>
            <DetailTitle>{t('Payment Method')}</DetailTitle>
            <DetailVal>{d.method}</DetailVal>
          </DetailRow>
        </BillBody>

        <BillFooter>
          <Button color='primary' width='100%' onClick={goHome}>
            {t('Go Home')}
          </Button>
        </BillFooter>
      </BillCard>
    </TxPage>
  )
}
